import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { externalSystemService, profileService, type ServiceContext } from '@/services';
import ExternalSystemFormDialog, { ExternalSystemRecord } from './ExternalSystemFormDialog';

export default function ExternalSystemManager() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchQuery, setSearchQuery] = useState('');
  const [formDialogOpen, setFormDialogOpen] = useState(false);

  // Fetch user profile to get company_id
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const result = await profileService.get(user.id);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  const getServiceContext = (): ServiceContext => ({
    companyId: companyId!,
    userId: user?.id,
  });

  // Fetch external system records
  const { data: records = [], isLoading } = useQuery({
    queryKey: ['external-systems', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      const result = await externalSystemService.list(companyId);
      if (result.error) throw result.error;
      return result.data as ExternalSystemRecord[];
    },
    enabled: !!companyId,
  });

  // Filtered records
  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const matchesSearch =
        searchQuery === '' ||
        record.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (record.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

      return matchesSearch;
    });
  }, [records, searchQuery]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: Omit<ExternalSystemRecord, 'id'>) => {
      if (!companyId) throw new Error('No company');
      const result = await externalSystemService.create(data, getServiceContext());
      if (result.error) throw result.error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['external-systems', companyId] });
      toast.success('External system created');
    },
    onError: (error) => {
      toast.error('Failed to create external system');
      console.error(error);
    },
  });

  const handleNewClick = () => {
    setFormDialogOpen(true);
  };

  const handleSave = async (data: Omit<ExternalSystemRecord, 'id'>) => {
    await createMutation.mutateAsync(data);
  };

  const handleSaveAndNew = async (data: Omit<ExternalSystemRecord, 'id'>) => {
    await createMutation.mutateAsync(data);
  };

  const handleRowClick = (record: ExternalSystemRecord) => {
    navigate(`/configuration/external-systems/${record.id}`);
  };

  if (!companyId) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Please set up your company profile first.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full sm:w-auto">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search external systems..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button onClick={handleNewClick}>
            <Plus className="w-4 h-4 mr-2" />
            New External System
          </Button>
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : filteredRecords.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {records.length === 0
            ? 'No external systems found. Create one to get started.'
            : 'No results match your search.'}
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <Table className="data-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRecords.map((record) => (
                <TableRow
                  key={record.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => handleRowClick(record)}
                >
                  <TableCell className="font-medium">{record.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {record.description || '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {record.created ? format(new Date(record.created), 'MMM d, yyyy') : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Form Dialog */}
      <ExternalSystemFormDialog
        open={formDialogOpen}
        onOpenChange={setFormDialogOpen}
        externalSystem={null}
        onSave={handleSave}
        onSaveAndNew={handleSaveAndNew}
      />
    </div>
  );
}
